import React from 'react';
import { Box, Card, Grid, Typography } from '@mui/material';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { IbssComponent } from '../../Core/BaseComponent/IbssComponent';
import { CateringMenuItemView, OrderView } from '../../../Pages/Shared/CateringOrders/Edit/EditCateringOrder';
import { CateringTemplateMenuItemView, CateringTemplateView } from '../../../Pages/OneLens/CateringTemplates/Edit/EditCateringTemplate';
import { appContext } from '../../../AppContext';
import IbssIconButton from '../../Buttons/IconButton/IbssIconButton';
import IbssTextField from '../../Inputs/TextField/IbssTextField';
import IbssButton from '../../Buttons/Button/IbssButton';
import IbssAccordion from '../Accordion/IbssAccordion';
import IbssCheckBox from '../../Inputs/CheckBox/IbssCheckBox';

class IbssCateringItemsAccordion extends IbssComponent<IProps, IState>
{
    private get labels() { return appContext().labels; }

    private getNoItemsMessage(classification: string): React.ReactElement 
    {
        const itemsAvailableWithoutFilter = this.props.orderItems?.filter(
            x => x.classification === classification && x.availableToOrder
        ).length === 0;

        let classificationLabel = '';

        switch (classification)
        {
            case 'MenuItemClassificationBeverage':
                classificationLabel = this.labels.funcCateringOrderBeverages_S.toLocaleLowerCase();
                break;
            case 'MenuItemClassificationSnack':
                classificationLabel = this.labels.funcCateringOrderSnacks_S.toLocaleLowerCase();
                break;
            case 'MenuItemClassificationFood':
                classificationLabel = this.labels.funcCateringOrderFoods_S.toLocaleLowerCase();
                break;
            default:
                break;
        }

        return (
            itemsAvailableWithoutFilter ?
                <Card sx={{ height: '100%', marginTop: '10px' }}>
                    <div style={{ position: 'relative', top: '15%', textAlign: 'center' }}>
                        <img height={300} width={300} src={`/images/NoCateringMenuAvailable.svg`} alt="" />
                        <Typography className="mb-1" variant="h5">{this.labels.funcCateringOrderNoItems_L.replace('{classification}', classificationLabel)}</Typography>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '80px' }}>
                            <div style={{ width: '350px' }}>{this.labels.funcCateringOrderNoItems_Message.replace('{classification}', classificationLabel)}</div>
                        </div>
                    </div>
                </Card>
                :
                <Card sx={{ height: '100%', marginTop: '10px' }}>
                    <div style={{ position: 'relative', top: '15%', textAlign: 'center' }}>
                        <img height={300} width={300} src={`/images/NoCateringMenuAvailable.svg`} alt="" />
                        <Typography className="mb-1" variant="h5">{this.labels.funcCateringOrderNoItemsWithFilter_L.replace('{classification}', classificationLabel)}</Typography>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '80px' }}>
                            <div style={{ width: '350px' }}>{this.labels.funcCateringOrderNoItemsWithFilter_Message.replace('{classification}', classificationLabel)}</div>
                        </div>
                    </div>
                </Card>
        );
    }    
      
    private getItemValue(item: CateringMenuItemView | CateringTemplateMenuItemView): number
    {
        if (this.props.itemInputType === 'quantity') 
        {
            return (item as CateringMenuItemView).quantity || 0;
        }
        
        // Checkbox mode
        return this.props.selectedMenuItemIds?.includes(item.menuItemId) ? 1 : 0;
    }

    private renderCheckboxControl(item: CateringMenuItemView | CateringTemplateMenuItemView): React.ReactElement
    {
        return (
            <Grid item xs={3} sx={{ alignSelf: 'center', textAlign: 'center', fontSize: '23px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
                    <IbssCheckBox
                        checked={this.getItemValue(item) > 0}
                        onClicked={(e: { target: { checked: boolean } }) => 
                            this.props?.handleItemCheckboxChange?.(item.menuItemId, e.target.checked)
                        }
                    />
                </Box>
            </Grid>
        );
    }

    private renderQuantityControl(item: CateringMenuItemView | CateringTemplateMenuItemView): React.ReactElement
    {
        const quantity = this.getItemValue(item);
        const validQuantity = !isNaN(quantity);

        return (
            <Grid item xs={3} sx={{ alignSelf: 'center', textAlign: 'center' }}>
                <IbssIconButton
                    className="mr-1"
                    aria-label="decrease"
                    disabled={quantity === 0}
                    onClick={() => this.props.onQuantityChange?.(item.menuItemId, quantity - 1)}
                >
                    <RemoveCircleOutlineIcon />
                </IbssIconButton>
                <IbssTextField
                    size="small"
                    type="number"
                    value={quantity}
                    error={!validQuantity}
                    sx={{
                        width: '4rem',
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none" },
                        "& input[type=number]": { MozAppearance: "textfield" }
                    }}
                    onChange={(e) => this.props.onInputChange?.(item.menuItemId, e.target.value)}
                />
                <IbssIconButton
                    className="ml-1"
                    aria-label="increase"
                    onClick={() => this.props.onQuantityChange?.(item.menuItemId, quantity + 1)}
                >
                    <AddCircleOutlineIcon />
                </IbssIconButton>
            </Grid>
        );
    }    
    
    private renderReadOnlyControl(item: CateringMenuItemView | CateringTemplateMenuItemView): React.ReactElement
    {
        const value = this.getItemValue(item);
        const validQuantity = !isNaN(value);

        return (
            <Grid item xs={3} sx={{ alignSelf: 'center', textAlign: 'center', display: 'flex', gap: '4rem' }}>
                <Typography variant="h6">{value}</Typography>
                <IbssButton
                    aria-label="remove"
                    disabled={value === 0 || !validQuantity}
                    onClick={() => this.props.onQuantityChange?.(item.menuItemId, 0)}
                >
                    {this.labels.HubLabelRemove}
                </IbssButton>
            </Grid>
        );
    }
    
    public render(): JSX.Element
    {
        const {
            classification,
            order,
            orderItems,
            availableMenuItemIds,
            userCateringRights,
            currency
        } = this.props;

        let newOrder = false;

        if ('orderId' in order) 
        {
            newOrder = order.orderId === "";
        } else if ('templateId' in order) 
        {
            newOrder = order.templateId === "";
        }
        
        const showZeroQuantityItems = newOrder || userCateringRights;        
          const filteredItems = orderItems?.filter(item => {
            if (item.classification === classification && item.meetsFilterRequirements) 
            {
                if (this.props.itemInputType === "checkbox") 
                {
                    return this.props.selectedMenuItemIds?.includes(item.menuItemId) || showZeroQuantityItems;
                }                
                return this.getItemValue(item) > 0 || showZeroQuantityItems;
            }
            return false;
        }) || [];

        const accordionItems = filteredItems.filter(item => item.showMenuItem).map(item =>
        {
            const orderIsDisabled = this.props.order instanceof OrderView? this.props.order.isDisabled : false;
            const modifyItemQuantityAllowed =
                !orderIsDisabled && (userCateringRights || (item.availableToOrder && availableMenuItemIds.includes(item.menuItemId)));

            return {
                key: item.menuItemId,
                summary: (
                    <Grid container>
                        <Grid item xs={2} md={3} lg={2} sx={{ display: 'flex', justifyContent: 'center', padding: item.imageURI ? '' : '40px 0px' }}>
                            {item.imageURI &&
                                <img width={100} height={100} src={item.imageURI} alt={item.name} />
                            }
                        </Grid>
                        <Grid item xs={6} md={5} lg={6} sx={{ alignSelf: 'center' }}>
                            <Box sx={{ ml: 2 }}>
                                <Typography variant="h6">{item.name}</Typography>
                                <Typography variant="body2">{item.description}</Typography>
                                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                    <div>
                                        {item.menuItemRestrictions.filter(x => x.section === 'Dietary').map(x =>
                                            <div key={x.name} className="d-flex" style={{ alignItems: 'center' }}>
                                                <img height={20} width={20} src={x.imageURI} alt={x.name} />
                                                <div className="ml-1">{x.name}</div>
                                            </div>
                                        )}
                                    </div>
                                    {item.menuItemRestrictions.some(x => x.section === 'Allergen') &&
                                        <div style={{ marginLeft: '40px' }}>
                                            <b>{this.labels.HubLabelContains}:</b>{' '}
                                            {item.menuItemRestrictions.filter(x => x.section === 'Allergen').map(x => x.name).join(', ')}
                                        </div>
                                    }
                                </div>
                            </Box>
                        </Grid>
                        <Grid item xs={1} sx={{ alignSelf: 'center' }}>
                            <Typography variant="body2">
                                {currency}{item.retailPrice.toFixed(2)}
                            </Typography>
                        </Grid>                        
                        
                        {modifyItemQuantityAllowed ? (
                            this.props.itemInputType === "checkbox" 
                                ? this.renderCheckboxControl(item)
                                : this.renderQuantityControl(item)
                        ) : (
                            this.renderReadOnlyControl(item)
                        )}
                    </Grid>
                ),
                details: (
                    item.nutritionalInformation && (
                        <div>
                            {this.labels.funcCateringOrderNutritionalInformation_S}:
                            <div>{item.nutritionalInformation}</div>
                        </div>
                    )
                ),
                hideExpand: item.nutritionalInformation === ''
            };
        });

        return (
            accordionItems.length > 0 ? (
                <Box className="menu-items-scroll">
                    <IbssAccordion onlyExpandOnIconClick={true} values={accordionItems} spacing="8px" />
                </Box>
            ) : this.getNoItemsMessage(classification)
        );
    }
}

export default IbssCateringItemsAccordion;


interface IProps
{
    classification: string;
    order: CateringTemplateView | OrderView;
    orderItems: CateringMenuItemView[] | CateringTemplateMenuItemView[];
    selectedMenuItemIds?: string[];
    availableMenuItemIds: string[];
    userCateringRights: boolean;
    currency: string;
    onQuantityChange?: (itemId: string, quantity: number) => void;
    onInputChange?: (itemId: string, input: string) => void;
    itemInputType: 'checkbox' | 'quantity'
    handleItemCheckboxChange?: (itemId: string, checked: boolean) => void;
}

interface IState
{ 
}